import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, AlertTriangle, Users, CheckCircle, XCircle, Trash2,
  LogOut, Plus, Edit, Eye, Loader2, ChevronDown, X, Camera, FileText,
  Clock, BarChart3, Search, Phone, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cases');
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [states, setStates] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Shokh Sandesh states
  const [shokhItems, setShokhItems] = useState([]);
  const [shokhLoading, setShokhLoading] = useState(false);
  const [shokhFilter, setShokhFilter] = useState('all');
  const [shokhStats, setShokhStats] = useState({ total_approved: 0, total_pending: 0 });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin');
  }, [isAuthenticated, authLoading, navigate]);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('case_type', filterType);
      const res = await axios.get(`${API}/api/admin/cases?${params}`, { headers });
      setCases(res.data.cases);
      setTotalPages(res.data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [page, filterStatus, filterType, token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/stats`, { headers });
      setStats(res.data);
    } catch (err) { console.error(err); }
  }, [token]);

  const fetchStates = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/states`); setStates(res.data); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => { if (token) { fetchCases(); fetchStats(); fetchStates(); } }, [token, fetchCases, fetchStats, fetchStates]);

  // Fetch Shokh Sandesh
  const fetchShokh = useCallback(async () => {
    setShokhLoading(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '50' });
      if (shokhFilter !== 'all') params.append('status', shokhFilter);
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/shokh-sandesh?${params}`, { headers }),
        axios.get(`${API}/api/shokh-stats`)
      ]);
      setShokhItems(itemsRes.data.items);
      setShokhStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setShokhLoading(false); }
  }, [token, shokhFilter]);

  useEffect(() => { if (token && activeTab === 'shokh') fetchShokh(); }, [token, activeTab, fetchShokh]);

  const handleShokhAction = async (id, action) => {
    try {
      if (action === 'delete') {
        await axios.delete(`${API}/api/admin/shokh-sandesh/${id}`, { headers });
        toast.success('हटाया गया');
      } else {
        await axios.post(`${API}/api/admin/shokh-sandesh/${id}/${action}`, {}, { headers });
        toast.success(action === 'approve' ? 'स्वीकृत' : 'अस्वीकार');
      }
      fetchShokh();
    } catch (err) { toast.error('Error'); }
  };

  const handleApprove = async (caseId) => {
    try {
      await axios.post(`${API}/api/admin/cases/${caseId}/approve`, {}, { headers });
      toast.success('केस स्वीकृत!');
      fetchCases(); fetchStats();
    } catch (err) { toast.error('Error approving case'); }
  };

  const handleReject = async (caseId) => {
    try {
      await axios.post(`${API}/api/admin/cases/${caseId}/reject`, {}, { headers });
      toast.success('केस अस्वीकार!');
      fetchCases(); fetchStats();
    } catch (err) { toast.error('Error rejecting case'); }
  };

  const handleDelete = async (caseId) => {
    if (!window.confirm('क्या आप इस केस को हटाना चाहते हैं?')) return;
    try {
      await axios.delete(`${API}/api/admin/cases/${caseId}`, { headers });
      toast.success('केस हटाया गया!');
      fetchCases(); fetchStats();
    } catch (err) { toast.error('Error deleting case'); }
  };

  const handleFound = async (caseId) => {
    try {
      await axios.post(`${API}/api/admin/cases/${caseId}/found`, {}, { headers });
      toast.success('केस "मिल गया" के रूप में चिह्नित!');
      fetchCases(); fetchStats();
    } catch (err) { toast.error('Error marking case as found'); }
  };

  const handleView = async (caseId) => {
    try {
      const res = await axios.get(`${API}/api/admin/cases/${caseId}`, { headers });
      setSelectedCase(res.data);
      setShowViewModal(true);
    } catch (err) { toast.error('Error loading case'); }
  };

  const handleEdit = async (caseId) => {
    try {
      const res = await axios.get(`${API}/api/admin/cases/${caseId}`, { headers });
      setSelectedCase(res.data);
      setShowEditModal(true);
    } catch (err) { toast.error('Error loading case'); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  const filteredCases = cases.filter(c =>
    !searchQuery || c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.case_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    found: 'bg-blue-100 text-blue-800'
  };
  const statusLabels = { pending: 'लंबित', approved: 'स्वीकृत', rejected: 'अस्वीकार', found: 'मिल गया' };
  const genderMap = { male: 'पुरुष', female: 'महिला', other: 'अन्य' };
  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <img src="/gumsuda-logo.png" alt="गुमसुदा" className="h-9 w-auto" />
            <div>
              <span className="font-outfit font-bold text-lg text-zinc-900">Admin Panel</span>
              <span className="text-xs text-zinc-500 ml-2 hidden sm:inline">({user?.email})</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-700 hidden sm:block">साइट देखें</Link>
            <Button variant="outline" size="sm" onClick={handleLogout} className="rounded-lg" data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1" />Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="कुल केस" value={stats.total_cases} icon={LayoutDashboard} color="bg-zinc-900" />
            <StatCard label="लापता" value={stats.total_missing} icon={AlertTriangle} color="bg-red-600" />
            <StatCard label="अज्ञात" value={stats.total_unknown} icon={Users} color="bg-amber-600" />
            <StatCard label="लंबित" value={stats.pending} icon={Clock} color="bg-orange-500" />
            <StatCard label="स्वीकृत" value={stats.approved} icon={CheckCircle} color="bg-green-600" />
            <StatCard label="मिल गया" value={stats.found || 0} icon={CheckCircle} color="bg-blue-600" />
            <StatCard label="अस्वीकार" value={stats.rejected} icon={XCircle} color="bg-red-500" />
            <StatCard label="Tips" value={stats.total_tips || 0} icon={FileText} color="bg-violet-600" />
          </div>
        )}

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-4">
          <Button variant={activeTab === 'cases' ? 'default' : 'outline'} onClick={() => setActiveTab('cases')} className="rounded-lg" data-testid="admin-tab-cases">
            <AlertTriangle className="w-4 h-4 mr-2" />केस ({stats ? stats.total_missing + stats.total_unknown : 0})
          </Button>
          <Button variant={activeTab === 'shokh' ? 'default' : 'outline'} onClick={() => setActiveTab('shokh')} className="rounded-lg" data-testid="admin-tab-shokh">
            <Heart className="w-4 h-4 mr-2" />शोक संदेश ({shokhStats.total_approved + shokhStats.total_pending})
          </Button>
        </div>

        {activeTab === 'cases' && (<>
        {/* Toolbar */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center flex-1">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input placeholder="नाम या केस ID से खोजें..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 rounded-lg h-10" data-testid="admin-search" />
              </div>
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                <SelectTrigger className="w-32 rounded-lg h-10" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">सभी Status</SelectItem>
                  <SelectItem value="pending">लंबित</SelectItem>
                  <SelectItem value="approved">स्वीकृत</SelectItem>
                  <SelectItem value="rejected">अस्वीकार</SelectItem>
                  <SelectItem value="found">मिल गया</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(1); }}>
                <SelectTrigger className="w-32 rounded-lg h-10" data-testid="admin-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">सभी Type</SelectItem>
                  <SelectItem value="missing">लापता</SelectItem>
                  <SelectItem value="unknown">अज्ञात</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setSelectedCase(null); setShowCreateModal(true); }} className="bg-red-600 hover:bg-red-700 rounded-lg h-10" data-testid="admin-create-case-btn">
              <Plus className="w-4 h-4 mr-2" />नया केस बनाएं
            </Button>
          </div>
        </div>

        {/* Cases Table */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-zinc-400" /></div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-20 text-zinc-500">कोई केस नहीं मिला</div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">फोटो</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">केस ID</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">नाम</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">प्रकार</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">शहर</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">स्टेटस</th>
                      <th className="text-left px-4 py-3 font-medium text-zinc-600">तारीख</th>
                      <th className="text-right px-4 py-3 font-medium text-zinc-600">एक्शन</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {filteredCases.map((c) => (
                      <tr key={c.case_id} className="hover:bg-zinc-50 transition-colors" data-testid={`case-row-${c.case_id}`}>
                        <td className="px-4 py-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-zinc-100">
                            {c.photo_url ? <img src={getPhotoUrl(c.photo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-400"><Users className="w-5 h-5" /></div>}
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">{c.case_id}</td>
                        <td className="px-4 py-3 font-medium text-zinc-900">{c.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${c.case_type === 'missing' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {c.case_type === 'missing' ? 'लापता' : 'अज्ञात'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-zinc-600">{c.city}, {c.state}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[c.status]}`}>{statusLabels[c.status]}</span>
                        </td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(c.created_at).toLocaleDateString('hi-IN')}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => handleView(c.case_id)} data-testid={`view-${c.case_id}`}><Eye className="w-4 h-4 text-zinc-600" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => handleEdit(c.case_id)} data-testid={`edit-${c.case_id}`}><Edit className="w-4 h-4 text-blue-600" /></Button>
                            {c.status === 'pending' && (
                              <>
                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => handleApprove(c.case_id)} data-testid={`approve-${c.case_id}`}><CheckCircle className="w-4 h-4 text-green-600" /></Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => handleReject(c.case_id)} data-testid={`reject-${c.case_id}`}><XCircle className="w-4 h-4 text-orange-600" /></Button>
                              </>
                            )}
                            {c.status === 'approved' && (
                              <Button variant="ghost" size="sm" className="h-8 rounded-lg text-xs px-2 text-blue-600" onClick={() => handleFound(c.case_id)} data-testid={`found-${c.case_id}`}>मिल गया</Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => handleDelete(c.case_id)} data-testid={`delete-${c.case_id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y divide-zinc-100">
                {filteredCases.map((c) => (
                  <div key={c.case_id} className="p-4" data-testid={`case-card-${c.case_id}`}>
                    <div className="flex gap-3">
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-zinc-100 flex-shrink-0">
                        {c.photo_url ? <img src={getPhotoUrl(c.photo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-zinc-400"><Users className="w-6 h-6" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-zinc-900 truncate">{c.name}</h3>
                            <p className="text-xs text-zinc-500">{c.case_id} | {c.city}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[c.status]}`}>{statusLabels[c.status]}</span>
                        </div>
                        <div className="flex gap-1 mt-2">
                          <Button variant="outline" size="sm" className="h-7 text-xs rounded-md px-2" onClick={() => handleView(c.case_id)}>देखें</Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs rounded-md px-2" onClick={() => handleEdit(c.case_id)}>Edit</Button>
                          {c.status === 'pending' && <Button size="sm" className="h-7 text-xs rounded-md px-2 bg-green-600 hover:bg-green-700" onClick={() => handleApprove(c.case_id)}>Approve</Button>}
                          {c.status === 'pending' && <Button size="sm" className="h-7 text-xs rounded-md px-2 bg-orange-500 hover:bg-orange-600" onClick={() => handleReject(c.case_id)}>Reject</Button>}
                          {c.status === 'approved' && <Button size="sm" className="h-7 text-xs rounded-md px-2 bg-blue-600 hover:bg-blue-700" onClick={() => handleFound(c.case_id)}>मिल गया</Button>}
                          <Button variant="outline" size="sm" className="h-7 text-xs rounded-md px-2 text-red-600 border-red-200" onClick={() => handleDelete(c.case_id)}>Delete</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-4 border-t border-zinc-100">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>पिछला</Button>
                  <span className="text-sm text-zinc-600">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>अगला</Button>
                </div>
              )}
            </>
          )}
        </div>
      </>)}

      {/* ===== SHOKH SANDESH TAB ===== */}
      {activeTab === 'shokh' && (
        <div>
          <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex gap-2">
                {['all', 'pending', 'approved', 'rejected'].map(s => (
                  <Button key={s} variant={shokhFilter === s ? 'default' : 'outline'} size="sm" className="rounded-lg"
                    onClick={() => setShokhFilter(s)}>
                    {s === 'all' ? 'सभी' : s === 'pending' ? `लंबित (${shokhStats.total_pending})` : s === 'approved' ? `स्वीकृत (${shokhStats.total_approved})` : 'अस्वीकार'}
                  </Button>
                ))}
              </div>
              <Link to="/admin/shokh-create">
                <Button className="bg-gray-900 hover:bg-gray-800 rounded-lg h-10"><Plus className="w-4 h-4 mr-2" />नया शोक संदेश</Button>
              </Link>
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            {shokhLoading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-zinc-400" /></div>
            ) : shokhItems.length === 0 ? (
              <div className="text-center py-20 text-zinc-500"><Heart className="w-12 h-12 mx-auto mb-3 text-zinc-300" /><p>कोई शोक संदेश नहीं</p></div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {shokhItems.map(item => (
                  <div key={item.sandesh_id} className="flex items-center gap-4 p-4 hover:bg-zinc-50" data-testid={`shokh-admin-${item.sandesh_id}`}>
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-zinc-100 flex-shrink-0">
                      {item.photo_url ? <img src={getPhotoUrl(item.photo_url)} alt={item.name} className="w-full h-full object-cover" /> : <Heart className="w-6 h-6 text-zinc-300 m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-zinc-900 truncate">{item.name}</h4>
                      <p className="text-xs text-zinc-500">{item.city}, {item.state} | निधन: {item.date_of_death}</p>
                      <p className="text-xs text-zinc-400 truncate">{item.message?.substring(0, 60)}...</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${item.status === 'approved' ? 'bg-green-100 text-green-700' : item.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
                      {item.status === 'approved' ? 'स्वीकृत' : item.status === 'pending' ? 'लंबित' : 'अस्वीकार'}
                    </span>
                    <div className="flex gap-1 flex-shrink-0">
                      {item.status === 'pending' && (
                        <>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => handleShokhAction(item.sandesh_id, 'approve')} data-testid={`shokh-approve-${item.sandesh_id}`}><CheckCircle className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => handleShokhAction(item.sandesh_id, 'reject')} data-testid={`shokh-reject-${item.sandesh_id}`}><XCircle className="w-4 h-4" /></Button>
                        </>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-400 hover:bg-zinc-50" onClick={() => handleShokhAction(item.sandesh_id, 'delete')} data-testid={`shokh-delete-${item.sandesh_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && <CaseFormModal title="नया केस बनाएं" states={states} token={token} onClose={() => setShowCreateModal(false)} onSuccess={() => { setShowCreateModal(false); fetchCases(); fetchStats(); toast.success('केस बनाया गया!'); }} />}
        {showEditModal && selectedCase && <CaseFormModal title="केस एडिट करें" states={states} token={token} editData={selectedCase} onClose={() => setShowEditModal(false)} onSuccess={() => { setShowEditModal(false); fetchCases(); fetchStats(); toast.success('केस अपडेट किया गया!'); }} />}
        {showViewModal && selectedCase && <CaseViewModal caseData={selectedCase} onClose={() => setShowViewModal(false)} />}
      </AnimatePresence>
    </div>
    </div>
  );
};

// ===== STAT CARD =====
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-zinc-900">{value}</p>
        <p className="text-xs text-zinc-500">{label}</p>
      </div>
    </div>
  </div>
);

// ===== CASE VIEW MODAL =====
const CaseViewModal = ({ caseData, onClose }) => {
  const isMissing = caseData.case_type === 'missing';
  const genderMap = { male: 'पुरुष', female: 'महिला', other: 'अन्य' };
  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="view-case-modal">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-outfit font-bold text-xl">केस विवरण - {caseData.case_id}</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          {/* Photo + Basic */}
          <div className="flex gap-6">
            {caseData.photo_url && (
              <div className="w-32 h-32 rounded-xl overflow-hidden bg-zinc-100 flex-shrink-0">
                <img src={getPhotoUrl(caseData.photo_url)} alt={caseData.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div>
              <h3 className="font-outfit font-bold text-2xl text-zinc-900">{caseData.name}</h3>
              <p className="text-zinc-500">{caseData.age} वर्ष, {genderMap[caseData.gender] || caseData.gender}</p>
              <div className="flex gap-2 mt-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${isMissing ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{isMissing ? 'लापता' : 'अज्ञात'}</span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${caseData.status === 'approved' ? 'bg-green-100 text-green-700' : caseData.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{caseData.status}</span>
              </div>
            </div>
          </div>

          {/* Physical Description */}
          <ViewSection title="शारीरिक विवरण">
            <ViewRow label="ऊंचाई" value={caseData.height} />
            <ViewRow label="रंग" value={caseData.complexion} />
            <ViewRow label="पहचान चिह्न" value={caseData.identifying_marks} />
            <ViewRow label="शारीरिक विवरण" value={caseData.physical_details} />
            <ViewRow label="पहनावा" value={caseData.clothing} />
          </ViewSection>

          {/* Location */}
          <ViewSection title={isMissing ? 'अंतिम बार देखा गया' : 'पाया गया'}>
            <ViewRow label="स्थान" value={isMissing ? caseData.last_seen_location : caseData.found_location} />
            <ViewRow label="तारीख" value={isMissing ? caseData.last_seen_date : caseData.found_date} />
            <ViewRow label="समय" value={isMissing ? caseData.last_seen_time : caseData.found_time} />
            <ViewRow label="शहर" value={caseData.city} />
            <ViewRow label="राज्य" value={caseData.state} />
            {!isMissing && <ViewRow label="वर्तमान हिरासत" value={caseData.current_custody} />}
            {!isMissing && <ViewRow label="सामान" value={caseData.belongings} />}
          </ViewSection>

          {/* Reporter */}
          <ViewSection title="रिपोर्टर जानकारी">
            <ViewRow label="नाम" value={caseData.reporter_name} />
            <ViewRow label="मोबाइल" value={caseData.reporter_mobile} />
            <ViewRow label="ईमेल" value={caseData.reporter_email} />
            <ViewRow label="संबंध" value={caseData.reporter_relation} />
            <ViewRow label="पता" value={caseData.reporter_address} />
            <ViewRow label="पुलिस स्टेशन" value={caseData.police_station} />
          </ViewSection>

          {caseData.additional_info && (
            <ViewSection title="अतिरिक्त जानकारी">
              <p className="text-sm text-zinc-700">{caseData.additional_info}</p>
            </ViewSection>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ViewSection = ({ title, children }) => (
  <div className="border border-zinc-200 rounded-lg overflow-hidden">
    <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
      <h4 className="font-outfit font-semibold text-sm text-zinc-700">{title}</h4>
    </div>
    <div className="p-4 grid grid-cols-2 gap-3">{children}</div>
  </div>
);

const ViewRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs text-zinc-500 block">{label}</span>
      <span className="text-sm font-medium text-zinc-900">{value}</span>
    </div>
  );
};

// ===== CASE FORM MODAL (Create / Edit) =====
const CaseFormModal = ({ title, states, token, editData, onClose, onSuccess }) => {
  const isEdit = !!editData;
  const [loading, setLoading] = useState(false);
  const [caseType, setCaseType] = useState(editData?.case_type || 'missing');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(editData?.photo_url ? (editData.photo_url.startsWith('http') ? editData.photo_url : `${API}/api/files/${editData.photo_url}`) : null);
  const [firCopy, setFirCopy] = useState(null);
  const [formData, setFormData] = useState({
    name: editData?.name || '',
    age: editData?.age?.toString() || '',
    gender: editData?.gender || 'male',
    height: editData?.height || '',
    complexion: editData?.complexion || '',
    identifying_marks: editData?.identifying_marks || '',
    physical_details: editData?.physical_details || '',
    clothing: editData?.clothing || '',
    city: editData?.city || '',
    state: editData?.state || '',
    last_seen_location: editData?.last_seen_location || '',
    last_seen_date: editData?.last_seen_date || '',
    last_seen_time: editData?.last_seen_time || '',
    found_location: editData?.found_location || '',
    found_date: editData?.found_date || '',
    found_time: editData?.found_time || '',
    current_custody: editData?.current_custody || '',
    belongings: editData?.belongings || '',
    reporter_name: editData?.reporter_name || '',
    reporter_mobile: editData?.reporter_mobile || '',
    reporter_email: editData?.reporter_email || '',
    reporter_relation: editData?.reporter_relation || '',
    reporter_address: editData?.reporter_address || '',
    police_station: editData?.police_station || '',
    additional_info: editData?.additional_info || '',
    status: editData?.status || 'approved',
  });

  const handleChange = (e) => { setFormData(prev => ({ ...prev, [e.target.name]: e.target.value })); };
  const handleSelect = (name, value) => { setFormData(prev => ({ ...prev, [name]: value })); };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('case_type', caseType);
      Object.keys(formData).forEach(key => {
        if (formData[key]) fd.append(key, formData[key]);
      });
      if (photo) fd.append('photo', photo);
      if (firCopy) fd.append('fir_copy', firCopy);

      if (isEdit) {
        await axios.put(`${API}/api/admin/cases/${editData.case_id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/api/admin/cases/create`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Error saving case');
    } finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="case-form-modal">
        <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-outfit font-bold text-xl">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Case Type & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">केस प्रकार *</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setCaseType('missing')} className={`flex-1 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${caseType === 'missing' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-zinc-600 border-zinc-200'}`}>लापता</button>
                <button type="button" onClick={() => setCaseType('unknown')} className={`flex-1 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${caseType === 'unknown' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-zinc-600 border-zinc-200'}`}>अज्ञात</button>
              </div>
            </div>
            {isEdit && (
              <div>
                <Label className="text-sm font-medium">स्टेटस</Label>
                <Select value={formData.status} onValueChange={(v) => handleSelect('status', v)}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">लंबित</SelectItem>
                    <SelectItem value="approved">स्वीकृत</SelectItem>
                    <SelectItem value="rejected">अस्वीकार</SelectItem>
                    <SelectItem value="found">मिल गया</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Photo */}
          <div>
            <Label className="text-sm font-medium mb-2 block">फोटो</Label>
            <label className="block cursor-pointer">
              <div className={`w-32 h-32 rounded-xl overflow-hidden border-2 border-dashed ${photoPreview ? 'border-green-400' : 'border-zinc-300'} flex items-center justify-center bg-zinc-50`}>
                {photoPreview ? <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" /> : <div className="text-center"><Camera className="w-8 h-8 text-zinc-400 mx-auto mb-1" /><p className="text-xs text-zinc-500">चुनें</p></div>}
              </div>
              <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="form-photo-input" />
            </label>
          </div>

          {/* Person Info */}
          <FormSection title="व्यक्ति की जानकारी">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label={`पूरा नाम ${caseType === 'missing' ? '*' : ''}`} name="name" value={formData.name} onChange={handleChange} required={caseType === 'missing'} placeholder={caseType === 'unknown' ? 'अज्ञात (वैकल्पिक)' : 'व्यक्ति का नाम'} />
              <FormField label="उम्र *" name="age" type="number" value={formData.age} onChange={handleChange} required placeholder="वर्ष" />
              <div>
                <Label className="text-sm text-zinc-600">लिंग *</Label>
                <Select value={formData.gender} onValueChange={(v) => handleSelect('gender', v)}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">पुरुष</SelectItem>
                    <SelectItem value="female">महिला</SelectItem>
                    <SelectItem value="other">अन्य</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <FormField label="ऊंचाई" name="height" value={formData.height} onChange={handleChange} placeholder="जैसे: 5.5 फीट" />
              <FormField label="रंग" name="complexion" value={formData.complexion} onChange={handleChange} placeholder="गोरा/सांवला" />
              <FormField label="पहचान चिह्न" name="identifying_marks" value={formData.identifying_marks} onChange={handleChange} placeholder="कोई विशेष निशान" />
              <FormField label="शारीरिक विवरण" name="physical_details" value={formData.physical_details} onChange={handleChange} placeholder="अन्य विवरण" />
              <FormField label="पहनावा" name="clothing" value={formData.clothing} onChange={handleChange} placeholder="अंतिम बार पहने कपड़े" />
            </div>
          </FormSection>

          {/* Location Info */}
          <FormSection title={caseType === 'missing' ? 'अंतिम बार देखा गया' : 'पाया गया'}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {caseType === 'missing' ? (
                <>
                  <FormField label="स्थान *" name="last_seen_location" value={formData.last_seen_location} onChange={handleChange} required placeholder="अंतिम स्थान" />
                  <FormField label="तारीख *" name="last_seen_date" type="date" value={formData.last_seen_date} onChange={handleChange} required />
                  <FormField label="समय" name="last_seen_time" type="time" value={formData.last_seen_time} onChange={handleChange} />
                </>
              ) : (
                <>
                  <FormField label="स्थान *" name="found_location" value={formData.found_location} onChange={handleChange} required placeholder="पाया गया स्थान" />
                  <FormField label="तारीख *" name="found_date" type="date" value={formData.found_date} onChange={handleChange} required />
                  <FormField label="समय" name="found_time" type="time" value={formData.found_time} onChange={handleChange} />
                  <FormField label="वर्तमान हिरासत" name="current_custody" value={formData.current_custody} onChange={handleChange} placeholder="कहाँ है अभी" />
                  <FormField label="सामान" name="belongings" value={formData.belongings} onChange={handleChange} placeholder="पास में मिला सामान" />
                </>
              )}
              <FormField label="शहर *" name="city" value={formData.city} onChange={handleChange} required placeholder="शहर" />
              <div>
                <Label className="text-sm text-zinc-600">राज्य *</Label>
                <Select value={formData.state} onValueChange={(v) => handleSelect('state', v)}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue placeholder="चुनें" /></SelectTrigger>
                  <SelectContent>{states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </FormSection>

          {/* Reporter Info */}
          <FormSection title="रिपोर्टर जानकारी">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="नाम *" name="reporter_name" value={formData.reporter_name} onChange={handleChange} required placeholder="रिपोर्टर का नाम" />
              <FormField label="मोबाइल *" name="reporter_mobile" value={formData.reporter_mobile} onChange={handleChange} required placeholder="10 अंक" />
              <FormField label="ईमेल" name="reporter_email" type="email" value={formData.reporter_email} onChange={handleChange} placeholder="ईमेल" />
              <FormField label="संबंध" name="reporter_relation" value={formData.reporter_relation} onChange={handleChange} placeholder="जैसे: पिता, माता" />
              <FormField label="पता" name="reporter_address" value={formData.reporter_address} onChange={handleChange} placeholder="पूरा पता" />
              <FormField label="पुलिस स्टेशन" name="police_station" value={formData.police_station} onChange={handleChange} placeholder="थाने का नाम" />
            </div>
          </FormSection>

          {/* Additional */}
          <FormSection title="अतिरिक्त जानकारी">
            <div>
              <Label className="text-sm text-zinc-600">अतिरिक्त जानकारी</Label>
              <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows={3} className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" placeholder="कोई अन्य जानकारी..." data-testid="form-additional-info" />
            </div>
            <div className="mt-3">
              <Label className="text-sm text-zinc-600">FIR कॉपी</Label>
              <Input type="file" accept=".pdf,.jpg,.png" onChange={(e) => setFirCopy(e.target.files[0])} className="mt-1 rounded-lg" data-testid="form-fir-input" />
            </div>
          </FormSection>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-12 rounded-lg">रद्द करें</Button>
            <Button type="submit" disabled={loading} className="flex-1 h-12 rounded-lg bg-red-600 hover:bg-red-700" data-testid="form-submit-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />सेव हो रहा है...</> : <>{isEdit ? 'अपडेट करें' : 'केस बनाएं'}</>}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const FormSection = ({ title, children }) => (
  <div className="border border-zinc-200 rounded-lg overflow-hidden">
    <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
      <h4 className="font-outfit font-semibold text-sm text-zinc-700">{title}</h4>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const FormField = ({ label, name, type = 'text', value, onChange, required, placeholder }) => (
  <div>
    <Label className="text-sm text-zinc-600">{label}</Label>
    <Input name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="mt-1 rounded-lg" data-testid={`form-${name}`} />
  </div>
);

export default AdminDashboard;
